import React, { useState, useRef } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { uploadTexture } from '../../firebase/materials';
import { X, Link2 } from 'lucide-react';

export const DeleteConfirmationModal = ({ isOpen, onClose, onConfirm, item, isDeleting }) => {
    if (!isOpen) return null;

    const itemName = item?.name || 'the selected item';

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow-xl w-full max-w-sm">
                <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100">Confirm Deletion</h3>
                <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
                    Are you sure you want to delete "{itemName}"? This action cannot be undone.
                </p>
                <div className="mt-6 flex justify-end space-x-3">
                    <button
                        onClick={onClose}
                        disabled={isDeleting}
                        className="px-4 py-2 rounded-lg font-medium bg-gray-200 hover:bg-gray-300 dark:bg-gray-600 dark:hover:bg-gray-500 text-gray-800 dark:text-gray-200 transition-colors disabled:opacity-50"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={isDeleting}
                        className="px-4 py-2 rounded-lg font-medium bg-red-600 hover:bg-red-700 text-white transition-colors disabled:opacity-50"
                    >
                        {isDeleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
};

export const ImageInfoModal = ({ isOpen, onClose, imageData }) => {
    if (!isOpen || !imageData) return null;

    const { name, description, price, linkUrl, imageUrl, currency } = imageData;

    const getCurrencySymbol = (currencyCode) => {
        if (currencyCode === 'EUR') return '€';
        return '$';
    };

    const getDomain = (url) => {
        try {
            return new URL(url).hostname.replace('www.', '');
        } catch (e) {
            return 'External Link';
        }
    }

    return (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-[70] p-4" onClick={onClose}>
            <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-lg relative overflow-hidden" onClick={e => e.stopPropagation()}>
                <button onClick={onClose} className="absolute top-3 right-3 p-1.5 rounded-full text-gray-500 bg-white/50 dark:bg-black/50 backdrop-blur-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors z-10">
                    <X className="h-5 w-5" />
                </button>
                
                {imageUrl && (
                    <div className="h-64 bg-gray-200 dark:bg-gray-700">
                        <img src={imageUrl} alt={name || 'Image'} className="w-full h-full object-cover" />
                    </div>
                )}

                <div className="p-6 space-y-4">
                    <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-200">{name || 'Image Details'}</h2>
                    
                    {description && (
                        <p className="text-gray-600 dark:text-gray-400">{description}</p>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 pt-2">
                        {price > 0 && (
                            <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg">
                                <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Price</p>
                                <p className="text-lg font-bold text-gray-800 dark:text-gray-200">
                                    {getCurrencySymbol(currency)}{price.toFixed(2)}
                                </p>
                            </div>
                        )}
                        {linkUrl && (
                             <div className="bg-gray-50 dark:bg-gray-700/50 p-3 rounded-lg flex items-center">
                                <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 group w-full">
                                    <div className="bg-blue-100 dark:bg-blue-900/50 p-2 rounded-full">
                                        <Link2 className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                                    </div>
                                    <div>
                                        <p className="text-xs text-gray-500 dark:text-gray-400 font-semibold uppercase">Link</p>
                                        <p className="text-sm font-semibold text-blue-600 dark:text-blue-400 group-hover:underline truncate">
                                            {getDomain(linkUrl)}
                                        </p>
                                    </div>
                                </a>
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
